import type { MarketListing as MarketListingType, BasicInfo, Benefits as BenefitsType, Assets, ContactInfo, InstallationRequirement, Status, Classification, PricingData } from '@wix/ambassador-devcenter-app-market-listing-v1-market-listing/types';
import type { Benefits, ImagesMedia, VideoMedia, MarketListingBase } from './types';
import { allCountries } from '../../constants/all-countries';

export class MarketListingWithFallback implements MarketListingBase {
  constructor(private readonly marketListings: MarketListingType[]) {}

  get isValid(): boolean {
    return (
      Boolean(this.appName) &&
      Boolean(this.appIcon) &&
      Boolean(this.description) &&
      Boolean(this.imagesMedia.length)
    );
  }

  get appName(): string {
    return this.getFirstNonBlank((listing) => listing?.basicInfo?.name) ?? '';
  }

  get appId(): string {
    return this.getFirstNonBlank((listing) => listing?.appId) ?? '';
  }

  get appIcon(): string {
    return this.getFirstNonBlank((listing) => listing.assetsMap?.appIcon?.assets?.[0]?.url) ?? '';
  }

  get appTeaser(): string {
    return this.getFirstNonBlank((listing) => listing?.basicInfo?.teaser) ?? '';
  }

  get benefits(): Benefits[] {
    return this.getFirstNonBlank((listing) => listing?.basicInfo?.benefits) ?? [];
  }

  get description(): string {
    return this.getFirstNonBlank((listing) => listing?.basicInfo?.longDescription) ?? '';
  }

  get demoUrl(): string | undefined {
    return this.getFirstNonBlank((listing) => listing?.basicInfo?.demoUrl) ?? undefined;
  }

  get appDependenciesIds(): string[] {
    const requiredApps = this.getFirstNonBlank((listing) => listing?.installationRequirement?.requiredApps) ?? [];

    return requiredApps.map((app) => {
      if (app.id) {
        return app.id;
      }

      throw new Error(
        `appId is missing in path: marketListing.installationRequirement.requiredApps`,
      );
    });
  }

  get imagesMedia(): ImagesMedia[] {
    const screenshots =
      (this.getFirstNonBlank((listing) => listing?.assetsMap?.screenshots) || {}).assets || [];

    return screenshots
      .filter((asset) => asset.mediaType === 'IMAGE')
      .map((asset) => ({
        url: asset.url || '',
        type: asset.mediaType as 'IMAGE',
      }));
  }

  get videoMedia(): VideoMedia[] {
    const videos =
      (this.getFirstNonBlank((listing) => listing?.assetsMap?.galleryVideos) || {}).assets || [];

    return videos.map((asset) => ({
      url: asset.url || '',
      type: asset.mediaType as 'YOUTUBE',
    }));
  }

  get isAppContentTranslatable(): boolean {
    return (
      this.getFirstNonBlank(
        (listing) => listing?.installationRequirement?.allowLanguageCustomization,
      ) ?? false
    );
  }

  get supportedLanguages(): string[] {
    return (
      this.getFirstNonBlank((listing) => listing?.installationRequirement?.includeLanguages) ?? []
    );
  }

  get geoAvailability(): {
    isAvailableWorldwide: boolean;
    availableCountries?: string[];
  } {
    const includeCountries =
      this.getFirstNonBlank((listing) => listing?.installationRequirement?.includeCountries) ?? [];
    const excludeCountries =
      this.getFirstNonBlank((listing) => listing?.installationRequirement?.excludeCountries) ?? [];

    if (includeCountries.length === 0 && excludeCountries.length === 0) {
      return { isAvailableWorldwide: true };
    }

    if (includeCountries.length > 0) {
      return {
        isAvailableWorldwide: false,
        availableCountries: includeCountries,
      };
    }

    return {
      isAvailableWorldwide: false,
      availableCountries: allCountries.filter(
        (country) => !excludeCountries.includes(country),
      ),
    };
  }

  get externalPricingPageUrl(): string | undefined {
    return this.getFirstNonBlank((listing) => listing?.pricingData?.externalPricingPageUrl) ?? undefined;
  }

  private get listingId(): string | null {
    return this.getFirstNonBlank((listing) => listing?.id) ?? null;
  }

  private get listingLanguageCode(): string | undefined {
    return this.getFirstNonBlank((listing) => listing?.languageCode) ?? undefined;
  }

  private get searchKeywords(): string[] {
    return this.getFirstNonBlank((listing) => listing?.basicInfo?.searchKeywords) ?? [];
  }

  private get categories(): string[] {
    return this.getFirstNonBlank((listing) => listing?.basicInfo?.categories) ?? [];
  }

  private get listingContactInfo(): ContactInfo | undefined {
    return this.getFirstNonBlank((listing) => listing?.contactInfo) ?? undefined;
  }

  private get listingInstallationRequirement(): InstallationRequirement | undefined {
    return this.getFirstNonBlank((listing) => listing?.installationRequirement) ?? undefined;
  }

  private get listingRevision(): string | undefined {
    return this.getFirstNonBlank((listing) => listing?.revision) ?? undefined;
  }

  private get listingCreatedDate(): Date | undefined {
    return this.getFirstNonBlank((listing) => listing?.createdDate) ?? undefined;
  }

  private get listingUpdatedDate(): Date | undefined {
    return this.getFirstNonBlank((listing) => listing?.updatedDate) ?? undefined;
  }

  private get listingStatus(): Status | undefined {
    return this.getFirstNonBlank((listing) => listing?.status) ?? undefined;
  }

  private get listingClassification(): Classification | undefined {
    return this.getFirstNonBlank((listing) => listing?.classification) ?? undefined;
  }

  private get listingPricingData(): PricingData | undefined {
    return this.getFirstNonBlank((listing) => listing?.pricingData) ?? undefined;
  }

  private get listingAssetsMap(): Record<string, Assets> {
    return this.getFirstNonBlank((listing) => listing?.assetsMap) ?? {};
  }

  private get appIconAssets(): Assets | undefined {
    return this.getFirstNonBlank((listing) => listing?.assetsMap?.appIcon) ?? undefined;
  }

  private get screenshotsAssets(): Assets | undefined {
    return this.getFirstNonBlank((listing) => listing?.assetsMap?.screenshots) ?? undefined;
  }

  private get galleryVideosAssets(): Assets | undefined {
    return this.getFirstNonBlank((listing) => listing?.assetsMap?.galleryVideos) ?? undefined;
  }

  toJSON(): MarketListingType {
    const basicInfoJson: BasicInfo = {
      name: this.appName,
      teaser: this.appTeaser,
      longDescription: this.description,
      benefits: this.benefits as BenefitsType[],
      searchKeywords: this.searchKeywords,
      demoUrl: this.demoUrl,
      categories: this.categories,
    };

    const baseAssetsMap: Record<string, Assets> = this.listingAssetsMap;
    const overrideAssetsMap: Record<string, Assets> = {
      ...(this.appIconAssets && { appIcon: this.appIconAssets }),
      ...(this.screenshotsAssets && { screenshots: this.screenshotsAssets }),
      ...(this.galleryVideosAssets && { galleryVideos: this.galleryVideosAssets }),
    };
    const mergedAssetsMap: Record<string, Assets> = {
      ...baseAssetsMap,
      ...overrideAssetsMap,
    };

    const ml: MarketListingType = {
      id: this.listingId,
      appId: this.appId,
      languageCode: this.listingLanguageCode,
      basicInfo: basicInfoJson,
      assetsMap: mergedAssetsMap,
      contactInfo: this.listingContactInfo,
      installationRequirement: this.listingInstallationRequirement,
      revision: this.listingRevision,
      createdDate: this.listingCreatedDate,
      updatedDate: this.listingUpdatedDate,
      status: this.listingStatus,
      classification: this.listingClassification,
      pricingData: this.listingPricingData,
    };

    return ml;
  }

  private getFirstNonBlank<T>(getter: (listing: MarketListingType) => T | undefined): T | undefined {
    for (const listing of this.marketListings) {
      const value = getter(listing);
      if (value !== undefined && value !== null && value !== '') {
        return value;
      }
    }

    return undefined;
  }

}
